import { useState } from "react";
import type { TenantUsageHistory } from "../../hooks/useAdminStatus";
import {
  ChartTooltip,
  tooltipStyles as tip,
  type ChartTooltipAnchor,
} from "./chart/ChartTooltip";
import { axisTicks, bucketRangeLabel, niceAxisMax, spendTooltipLine } from "../../lib/chartBuckets";
import styles from "./UsageChart.module.css";

const AXIS_W = 34;

type Hover = ChartTooltipAnchor & { readonly index: number };

// AI spend per bucket. 48 bars of deltaUsd with a full-height transparent
// hit-rect per bar that surfaces a portaled hover tooltip + a left y-axis.
export const UsageChart = ({ usage }: { usage: TenantUsageHistory }): JSX.Element => {
  const [hover, setHover] = useState<Hover | null>(null);
  const rawMax = Math.max(...usage.points.map((p) => p.deltaUsd), 0.0001);
  const axisMax = niceAxisMax(rawMax);
  const width = 480;
  const height = 120;
  const barGap = 2;
  const plotW = width - AXIS_W;
  const barWidth = (plotW - barGap * (usage.points.length - 1)) / usage.points.length;
  const ticks = axisTicks(axisMax, 3);
  const fmtUsd = (n: number): string => `$${n.toFixed(4)}`;
  const onEnter = (index: number) => (e: React.MouseEvent<SVGRectElement>): void => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover({ index, cx: r.left + r.width / 2, top: r.top, bottom: r.bottom });
  };
  const hovered = hover != null ? usage.points[hover.index] : undefined;
  return (
    <>
      <svg
        className={styles.usageChart}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="AI spend per bucket"
      >
        {ticks.map((t) => {
          const y = height - (t / axisMax) * (height - 14);
          if (t === 0) return null;
          return (
            <line
              key={t}
              className={styles.gridline}
              x1={AXIS_W}
              x2={width}
              y1={y}
              y2={y}
              aria-hidden
            />
          );
        })}
        <text className={styles.axisLabel} x={0} y={10} aria-hidden>
          {fmtUsd(axisMax)}
        </text>
        <text className={styles.axisLabel} x={0} y={height - 1} aria-hidden>
          0
        </text>
        {usage.points.map((p, i) => {
          const x = AXIS_W + i * (barWidth + barGap);
          const h = Math.max((p.deltaUsd / axisMax) * (height - 14), p.deltaUsd > 0 ? 2 : 0);
          return (
            <g key={p.t}>
              <rect
                className={styles.usageBarHit}
                x={x}
                y={0}
                width={barWidth}
                height={height}
                aria-label={`${bucketRangeLabel(p.t, usage.bucketSeconds)}: ${spendTooltipLine(p.deltaUsd, p.spentUsd)}`}
                onMouseEnter={onEnter(i)}
                onMouseLeave={() => setHover(null)}
              />
              <rect
                className={p.deltaUsd > 0 ? styles.usageBar : styles.usageBarEmpty}
                x={x}
                y={height - h - (p.deltaUsd > 0 ? 0 : 1)}
                width={barWidth}
                height={p.deltaUsd > 0 ? h : 1}
                rx={1}
              />
            </g>
          );
        })}
      </svg>
      {hover != null && hovered != null && (
        <ChartTooltip anchor={hover}>
          <div className={tip.tipHead}>
            <span className={tip.tipState}>AI spend</span>
            <span className={tip.tipTime}>{bucketRangeLabel(hovered.t, usage.bucketSeconds)}</span>
          </div>
          <div className={tip.tipBody}>{spendTooltipLine(hovered.deltaUsd, hovered.spentUsd)}</div>
        </ChartTooltip>
      )}
    </>
  );
};
